import { MapPin, Radio } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";

// Add { city, machine } here as each machine goes live — rows light up green automatically.
const LIVE = [{ city: "Ste. Anne, Manitoba", machine: "SnackPod™ One" }];
const WAITING = [
  "Winnipeg — Downtown",
  "Winnipeg — St. James",
  "Winnipeg — St. Vital",
  "Brandon",
  "Steinbach",
  "Selkirk",
  "Portage la Prairie",
  "Winkler",
];

export default function NowServing() {
  const live = LIVE.length;
  return (
    <section id="now-serving" data-testid="now-serving-section" className="relative py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
        <div>
          <Reveal>
            <Eyebrow>Now serving</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#16120E] sm:text-4xl lg:text-5xl">
              Watch Manitoba light up.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-stone-600 sm:text-lg">
              Every dot on this board is a real SnackSavvy machine, live and
              restocked. Right now it's waiting for its very first — and that
              could be your breakroom.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex items-center gap-5">
              <div className="font-display text-7xl font-extrabold leading-none text-[#1B365D] sm:text-8xl" data-testid="live-machine-count">
                {live}
              </div>
              <div className="max-w-[180px]">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[#16120E]">
                  {live === 1 ? "machine live" : "machines live"}
                </p>
                <p className="mt-1.5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[#A8861D]">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute h-full w-full animate-ping rounded-full bg-[#C9A227] opacity-70" style={live > 0 ? { display: "none" } : undefined} />
                    <span className="relative h-2 w-2 rounded-full bg-[#C9A227]" style={live > 0 ? { display: "none" } : undefined} />
                  </span>
                  {live === 0 ? "Awaiting first placement" : "Growing every month"}
                </p>
              </div>
            </div>
            <button
              data-testid="now-serving-cta"
              onClick={() => scrollToSection("quote")}
              className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#1B365D] px-6 py-3.5 font-semibold text-[#FBF7EF] transition-all hover:-translate-y-0.5 hover:bg-[#2A4A73] hover:shadow-[0_14px_40px_rgba(27,54,93,0.35)]"
            >
              <MapPin className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
              Be the first — claim a founding spot
            </button>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="network-status-panel"
            className="overflow-hidden rounded-3xl border border-black/10 bg-[#1B365D] shadow-[0_30px_90px_rgba(27,54,93,0.18)]"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <span className="flex items-center gap-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#FBF7EF]">
                <Radio className="h-4 w-4 text-[#C9A227]" />
                Live network status
              </span>
              <span className="rounded-full bg-[#C9A227]/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-[#E5C558]">
                Manitoba
              </span>
            </div>
            <ul className="divide-y divide-white/8">
              {LIVE.map((l, i) => (
                <li key={l.city} data-testid={`now-serving-live-${i}`} className="flex items-center justify-between gap-4 px-6 py-3.5">
                  <span className="flex items-center gap-3 text-sm font-medium text-[#FBF7EF]">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                      <span className="relative h-2 w-2 rounded-full bg-emerald-400" />
                    </span>
                    {l.city}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-300">
                    {l.machine} · live
                  </span>
                </li>
              ))}
              {WAITING.map((city, i) => (
                <li key={city} data-testid={`now-serving-waiting-${i}`} className="flex items-center justify-between gap-4 px-6 py-3.5">
                  <span className="flex items-center gap-3 text-sm font-medium text-[#FBF7EF]/55">
                    <span className="h-2 w-2 rounded-full border border-white/25" />
                    {city}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#FBF7EF]/40">
                    — open
                  </span>
                </li>
              ))}
            </ul>
            <div className="border-t border-white/10 px-6 py-4">
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#FBF7EF]/50">
                Founding locations appear here the day their machine ships
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
